import { ChangeDetectionStrategy, Component, computed, inject, signal } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSelectModule } from '@angular/material/select';
import type { AccommodationListItem } from '@models/accommodations';
import type { FishingSpotListItem } from '@models/fishing-spots';
import type {
  WaterUserMediaDetail,
  WaterUserMediaItem,
  WaterUserMediaUpdateRequest,
} from '@models/water-user-media-batches';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { CrmAccommodationsService } from '@services/crm-accommodations.service';
import { CrmFishingSpotsService } from '@services/crm-fishing-spots.service';
import { ToastService } from '@services/toast.service';
import { WaterUserMediaService } from '@services/water-user-media.service';
import { LocalizedTextEditorComponent } from '@shared/components/localized-text-editor/localized-text-editor.component';
import { extractYoutubeId, youtubeThumbnailUrl, youtubeUrlValidator } from '@utils/youtube';
import { forkJoin } from 'rxjs';

export interface WaterUserMediaEditFormData {
  waterId: number;
  mediaId: number;
}

interface MediaEditForm {
  tag: FormControl<string | null>;
  videoUrl: FormControl<string | null>;
  accommodationId: FormControl<number | null>;
  spotId: FormControl<number | null>;
  descriptionNl: FormControl<string | null>;
  descriptionEn: FormControl<string | null>;
  descriptionDe: FormControl<string | null>;
  descriptionIt: FormControl<string | null>;
  descriptionFr: FormControl<string | null>;
}

@Component({
  selector: 'app-water-user-media-edit-form',
  templateUrl: './water-user-media-edit-form.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    ReactiveFormsModule,
    MatDialogModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatButtonModule,
    MatProgressSpinnerModule,
    LocalizedTextEditorComponent,
    TranslatePipe,
  ],
})
export class WaterUserMediaEditFormComponent {
  private readonly mediaService = inject(WaterUserMediaService);
  private readonly accommodationsService = inject(CrmAccommodationsService);
  private readonly fishingSpotsService = inject(CrmFishingSpotsService);
  private readonly toastService = inject(ToastService);
  private readonly translate = inject(TranslateService);
  private readonly dialogRef = inject(MatDialogRef<WaterUserMediaEditFormComponent>);
  private readonly data = inject<WaterUserMediaEditFormData>(MAT_DIALOG_DATA);

  readonly tags = ['water', 'catches', 'facilities', 'video'];

  readonly isLoading = signal(true);
  readonly isSaving = signal(false);
  readonly detail = signal<WaterUserMediaDetail | null>(null);
  readonly accommodations = signal<AccommodationListItem[]>([]);
  readonly spots = signal<FishingSpotListItem[]>([]);

  readonly thumbUrl = computed<string | null>(() => {
    const d = this.detail();
    if (!d) return null;
    if (d.photo) return d.photo;
    const id = extractYoutubeId(d.videoUrl);
    return id ? youtubeThumbnailUrl(id) : null;
  });

  readonly form = new FormGroup<MediaEditForm>({
    tag: new FormControl(null),
    videoUrl: new FormControl(null, [youtubeUrlValidator]),
    accommodationId: new FormControl(null),
    spotId: new FormControl(null),
    descriptionNl: new FormControl(null),
    descriptionEn: new FormControl(null),
    descriptionDe: new FormControl(null),
    descriptionIt: new FormControl(null),
    descriptionFr: new FormControl(null),
  });

  constructor() {
    const { waterId, mediaId } = this.data;
    forkJoin({
      detail: this.mediaService.getMediaItem(waterId, mediaId),
      accommodations: this.accommodationsService.getAccommodations(waterId),
      spots: this.fishingSpotsService.getSpots(waterId),
    }).subscribe({
      next: ({ detail, accommodations, spots }) => {
        this.detail.set(detail);
        this.accommodations.set(accommodations);
        this.spots.set(spots);
        this.form.patchValue({
          tag: detail.tag,
          videoUrl: detail.videoUrl,
          accommodationId: detail.accommodationId,
          spotId: detail.spotId,
          descriptionNl: detail.descriptionNl,
          descriptionEn: detail.descriptionEn,
          descriptionDe: detail.descriptionDe,
          descriptionIt: detail.descriptionIt,
          descriptionFr: detail.descriptionFr,
        });
        this.isLoading.set(false);
      },
      error: () => {
        this.toastService.showError(this.translate.instant('waters.media.edit.load-failed'));
        this.dialogRef.close();
      },
    });
  }

  save(): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      this.toastService.showError(this.translate.instant('waters.media.edit.invalid'));
      return;
    }

    const raw = this.form.getRawValue();
    const dto: WaterUserMediaUpdateRequest = {
      tag: raw.tag,
      videoUrl: raw.videoUrl || null,
      accommodationId: raw.accommodationId,
      spotId: raw.spotId,
      descriptionNl: raw.descriptionNl || null,
      descriptionEn: raw.descriptionEn || null,
      descriptionDe: raw.descriptionDe || null,
      descriptionIt: raw.descriptionIt || null,
      descriptionFr: raw.descriptionFr || null,
    };

    this.isSaving.set(true);
    this.mediaService.updateMedia(this.data.waterId, this.data.mediaId, dto).subscribe({
      next: (updated: WaterUserMediaItem) => {
        this.isSaving.set(false);
        this.dialogRef.close(updated);
      },
      error: () => {
        this.isSaving.set(false);
        this.toastService.showError(this.translate.instant('waters.media.edit.save-failed'));
      },
    });
  }

  cancel(): void {
    this.dialogRef.close();
  }
}
